import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, Gift, Rocket, CheckCircle, ArrowRight } from "lucide-react";

/**
 * Birthday-mode call-to-action: adapts label & action based on user state.
 */
export default function BirthdayCTA({ days, hasItinerary, userColor, readinessScore }) {
  const navigate = useNavigate();
  const color = userColor || "#0ea5e9";

  if (days == null) return null;
  // Only show in birthday mode (≤30 days or post-birthday)
  if (days > 30) return null;

  let primary, secondary;

  if (days === 0) {
    primary = {
      label: hasItinerary ? "Launch Itinerary" : "Start Claiming!",
      icon: hasItinerary ? MapPin : Gift,
      to: hasItinerary ? "/my-itinerary" : "/my-deals",
    };
    secondary = {
      label: "See All Deals",
      to: "/my-deals",
    };
  } else if (days < 0) {
    primary = {
      label: "Claim My Deals",
      icon: Gift,
      to: "/my-deals",
    };
    secondary = hasItinerary ? { label: "View Route", to: "/my-itinerary" } : null;
  } else if (days <= 3) {
    primary = {
      label: hasItinerary ? "Final Review" : "Build Plan Now",
      icon: hasItinerary ? CheckCircle : Rocket,
      to: "/my-itinerary",
    };
    secondary = {
      label: "Prep Checklist",
      to: "/countdown",
    };
  } else {
    primary = {
      label: hasItinerary ? "Review Your Plan" : "Build Your Plan",
      icon: hasItinerary ? MapPin : Rocket,
      to: "/my-itinerary",
    };
    secondary = {
      label: "Browse Deals",
      to: "/my-deals",
    };
  }

  const PrimaryIcon = primary.icon;

  return (
    <div className="px-4 mb-4" data-testid="birthday-cta">
      <div className="flex gap-2">
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(primary.to)}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-white text-sm font-bold shadow-lg"
          style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
          data-testid="birthday-cta-primary"
        >
          <PrimaryIcon className="w-4 h-4" />
          {primary.label}
          <ArrowRight className="w-3.5 h-3.5 opacity-60" />
        </motion.button>
        {secondary && (
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate(secondary.to)}
            className="px-4 py-3 rounded-2xl text-sm font-bold border border-slate-200 bg-white text-slate-600 shadow-sm"
            data-testid="birthday-cta-secondary"
          >
            {secondary.label}
          </motion.button>
        )}
      </div>
      {/* Readiness bar */}
      {readinessScore != null && readinessScore > 0 && days > 0 && (
        <div className="mt-2 flex items-center gap-2" data-testid="readiness-bar">
          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${readinessScore}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${color}, ${color}aa)` }}
            />
          </div>
          <span className="text-[10px] font-bold text-slate-400">{readinessScore}% ready</span>
        </div>
      )}
    </div>
  );
}
